
import { addHours, subHours, format, isAfter, isBefore } from "date-fns";
import { Flight, Hotel } from "@/types/trip-types";

// Constants for standard hotel times and buffers
const DEFAULT_CHECKIN_TIME = "14:00"; // 2 PM
const DEFAULT_CHECKOUT_TIME = "12:00"; // 12 PM
const FLIGHT_ARRIVAL_BUFFER = 2; // hours after flight arrival
const FLIGHT_DEPARTURE_BUFFER = 3; // hours before flight departure

interface AdjustedHotel extends Hotel {
  originalCheckIn: Date;
  originalCheckOut: Date;
  adjustedCheckIn: Date;
  adjustedCheckOut: Date;
  adjustmentReason: {
    checkIn: string | null;
    checkOut: string | null;
  };
}

export const adjustHotelTimesBasedOnFlights = (
  hotel: Hotel,
  flights: Flight[]
): AdjustedHotel => {
  if (!flights.length) {
    // No flights, use standard hotel times
    const checkInDate = new Date(hotel.checkIn);
    checkInDate.setHours(14, 0, 0, 0); // Set to 2 PM
    
    const checkOutDate = new Date(hotel.checkOut);
    checkOutDate.setHours(12, 0, 0, 0); // Set to 12 PM
    
    return {
      ...hotel,
      originalCheckIn: hotel.checkIn,
      originalCheckOut: hotel.checkOut,
      adjustedCheckIn: checkInDate,
      adjustedCheckOut: checkOutDate,
      adjustmentReason: {
        checkIn: null,
        checkOut: null
      }
    };
  }

  // Sort flights chronologically by departure time
  const sortedFlights = [...flights].sort((a, b) => {
    const aDate = a.details?.departure?.time 
      ? new Date(a.details.departure.time) 
      : new Date(`${format(a.departureDate, "yyyy-MM-dd")}T${a.departureTime}`);
    
    const bDate = b.details?.departure?.time 
      ? new Date(b.details.departure.time) 
      : new Date(`${format(b.departureDate, "yyyy-MM-dd")}T${b.departureTime}`);
    
    return aDate.getTime() - bDate.getTime();
  });

  // Find relevant flights for this hotel stay
  const previousFlights = sortedFlights.filter(flight => {
    const arrivalDate = flight.details?.arrival?.time 
      ? new Date(flight.details.arrival.time) 
      : new Date(`${format(flight.arrivalDate, "yyyy-MM-dd")}T${flight.arrivalTime}`);
    
    // Arrival is before or on check-in day
    return isBefore(arrivalDate, new Date(hotel.checkIn)) || 
      format(arrivalDate, "yyyy-MM-dd") === format(new Date(hotel.checkIn), "yyyy-MM-dd");
  });

  const nextFlights = sortedFlights.filter(flight => {
    const departureDate = flight.details?.departure?.time 
      ? new Date(flight.details.departure.time) 
      : new Date(`${format(flight.departureDate, "yyyy-MM-dd")}T${flight.departureTime}`);
    
    // Departure is after or on check-out day
    return isAfter(departureDate, new Date(hotel.checkOut)) || 
      format(departureDate, "yyyy-MM-dd") === format(new Date(hotel.checkOut), "yyyy-MM-dd");
  });

  // Find closest previous flight (arriving before hotel)
  const closestPreviousFlight = previousFlights.length > 0 
    ? previousFlights[previousFlights.length - 1] 
    : null;

  // Find closest next flight (departing after hotel)
  const closestNextFlight = nextFlights.length > 0 
    ? nextFlights[0] 
    : null;

  // Calculate adjusted check-in time
  let adjustedCheckIn = new Date(hotel.checkIn);
  let checkInReason = null;

  if (closestPreviousFlight) {
    const arrivalDate = closestPreviousFlight.details?.arrival?.time 
      ? new Date(closestPreviousFlight.details.arrival.time) 
      : new Date(`${format(closestPreviousFlight.arrivalDate, "yyyy-MM-dd")}T${closestPreviousFlight.arrivalTime}`);
    
    // If arrival is on the same day as check-in
    if (format(arrivalDate, "yyyy-MM-dd") === format(new Date(hotel.checkIn), "yyyy-MM-dd")) {
      // Add buffer to arrival time
      adjustedCheckIn = addHours(arrivalDate, FLIGHT_ARRIVAL_BUFFER);
      checkInReason = `Adjusted to ${FLIGHT_ARRIVAL_BUFFER} hours after flight arrival`;
    } else {
      // Different day, use standard check-in time
      adjustedCheckIn.setHours(14, 0, 0, 0); // Set to 2 PM
    }
  } else {
    // No previous flight, use standard check-in time
    adjustedCheckIn.setHours(14, 0, 0, 0); // Set to 2 PM
  }

  // Calculate adjusted check-out time
  let adjustedCheckOut = new Date(hotel.checkOut);
  let checkOutReason = null;

  if (closestNextFlight) {
    const departureDate = closestNextFlight.details?.departure?.time 
      ? new Date(closestNextFlight.details.departure.time) 
      : new Date(`${format(closestNextFlight.departureDate, "yyyy-MM-dd")}T${closestNextFlight.departureTime}`);
    
    // If departure is on the same day as check-out
    if (format(departureDate, "yyyy-MM-dd") === format(new Date(hotel.checkOut), "yyyy-MM-dd")) {
      // Subtract buffer from departure time
      adjustedCheckOut = subHours(departureDate, FLIGHT_DEPARTURE_BUFFER);
      checkOutReason = `Adjusted to ${FLIGHT_DEPARTURE_BUFFER} hours before flight departure`;
    } else {
      // Different day, use standard check-out time
      adjustedCheckOut.setHours(12, 0, 0, 0); // Set to 12 PM
    }
  } else {
    // No next flight, use standard check-out time
    adjustedCheckOut.setHours(12, 0, 0, 0); // Set to 12 PM
  }

  return {
    ...hotel,
    originalCheckIn: hotel.checkIn,
    originalCheckOut: hotel.checkOut,
    adjustedCheckIn,
    adjustedCheckOut,
    adjustmentReason: {
      checkIn: checkInReason,
      checkOut: checkOutReason
    }
  };
};

// Add a function to format the time for display
export const formatAdjustedTime = (date: Date): string => {
  return format(date, "HH:mm");
};
